import { createSlice } from '@reduxjs/toolkit';
import { safeJsonParse } from '../../utils/safeJsonParse';

const initialState = {
  others: [],
  imgs_for_filters: [],
  loading: false,
  error: null,
};

const othersDataSlice = createSlice({
  name: 'others_data',
  initialState,
  reducers: {
    setOthers: (state, action) => {
      // console.log('setOthers action called with payload:', action.payload);

      // Сначала устанавливаем others, чтобы не потерять данные
      state.others = action.payload;

      // Проверяем, есть ли данные для обработки
      if (!action.payload || !Array.isArray(action.payload)) {
        // console.warn('setOthers: action.payload is not an array or is empty');
        state.imgs_for_filters = [];
        return;
      }

      // Ищем элементы с eng_name === 'img_for_filter'
      const imgsForFilter = action.payload.filter((rl) => rl.eng_name === 'img_for_filter');

      if (imgsForFilter.length === 0) {
        // console.warn('setOthers: no elements with eng_name === "img_for_filter" found');
        state.imgs_for_filters = [];
        return;
      }

      // Проверяем, что у первого элемента есть content
      if (!imgsForFilter[0] || !imgsForFilter[0].content) {
        // console.warn('setOthers: first img_for_filter element has no content');
        state.imgs_for_filters = [];
        return;
      }

      try {
        const filterContent = JSON.parse(imgsForFilter[0].content);

        if (!filterContent || !Array.isArray(filterContent) || filterContent.length === 0) {
          // console.warn('setOthers: filterContent is not a valid array');
          state.imgs_for_filters = [];
          return;
        }

        if (
          !filterContent[0] ||
          !filterContent[0].block_content ||
          !Array.isArray(filterContent[0].block_content)
        ) {
          // console.warn('setOthers: filterContent[0].block_content is not a valid array');
          state.imgs_for_filters = [];
          return;
        }

        if (!filterContent[0].block_content[0] || !filterContent[0].block_content[0].imgs_names) {
          // console.warn('setOthers: filterContent[0].block_content[0].imgs_names is missing');
          state.imgs_for_filters = [];
          return;
        }

        // Проверяем на "none" перед парсингом
        const imgsNamesValue = filterContent[0].block_content[0].imgs_names;
        if (!imgsNamesValue || imgsNamesValue.trim() === 'none') {
          state.imgs_for_filters = [];
          return;
        }

        const f_imgs = safeJsonParse(imgsNamesValue, []);
        const path = imgsForFilter[0].path;
        const blank_name = imgsForFilter[0].blank_name;

        if (!Array.isArray(f_imgs)) {
          // console.warn('setOthers: f_imgs is not an array');
          state.imgs_for_filters = [];
          return;
        }

        const processedImgs = f_imgs.map((imgPath) => {
          const parts = imgPath.split(/[/\\]/);
          const fileName = parts[parts.length - 1];
          return 'https://' + path + blank_name + '/' + fileName;
        });

        // Сигнатуры по индексу (как ссылки в главном слайдере) — в word
        let signaturesArr = [];
        const imgsBlock = filterContent[0].block_content[0];
        if (imgsBlock?.imgs_signatures && imgsBlock.imgs_signatures !== 'none' && imgsBlock.imgs_signatures !== '[]') {
          try {
            const parsed = typeof imgsBlock.imgs_signatures === 'string'
              ? JSON.parse(imgsBlock.imgs_signatures)
              : imgsBlock.imgs_signatures;
            signaturesArr = Array.isArray(parsed) ? parsed : [];
          } catch {
            signaturesArr = [];
          }
        }

        const imgsForFiltersArray = processedImgs.map((el, i) => {
          const raw = signaturesArr[i];
          const word = raw != null && String(raw).trim() !== '' && String(raw).trim().toLowerCase() !== 'none'
            ? String(raw).trim()
            : (filterContent[0].block_content[i + 1]?.content_text || '').trim();
          return { img: el, word: word || '' };
        });

        state.imgs_for_filters = imgsForFiltersArray;
        // console.log('state.others', state.others);
        // console.log('imgs_for_filters processed successfully:', state.imgs_for_filters);
      } catch (error) {
        // console.error('setOthers: Error processing filter content:', error);
        state.imgs_for_filters = [];
      }
    },
    addOther: (state, action) => {
      state.others.push(action.payload);
    },
    removeOther: (state, action) => {
      state.others = state.others.filter((other) => other.id !== action.payload);
    },
    updateOther: (state, action) => {
      const { id, updates } = action.payload;
      const otherIndex = state.others.findIndex((other) => other.id === id);
      if (otherIndex !== -1) {
        state.others[otherIndex] = { ...state.others[otherIndex], ...updates };
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearOthers: (state) => {
      state.others = [];
      state.error = null;
    },
    setImgsForFilters: (state, action) => {
      state.imgs_for_filters = action.payload;
    },
    addImgForFilter: (state, action) => {
      state.imgs_for_filters.push(action.payload);
    },
    removeImgForFilter: (state, action) => {
      state.imgs_for_filters = state.imgs_for_filters.filter((img) => img.id !== action.payload);
    },
    updateImgForFilter: (state, action) => {
      const { id, updates } = action.payload;
      const imgIndex = state.imgs_for_filters.findIndex((img) => img.id === id);
      if (imgIndex !== -1) {
        state.imgs_for_filters[imgIndex] = { ...state.imgs_for_filters[imgIndex], ...updates };
      }
    },
    clearImgsForFilters: (state) => {
      state.imgs_for_filters = [];
    },
  },
});

export const {
  setOthers,
  addOther,
  removeOther,
  updateOther,
  setLoading,
  setError,
  clearOthers,
  setImgsForFilters,
  addImgForFilter,
  removeImgForFilter,
  updateImgForFilter,
  clearImgsForFilters,
} = othersDataSlice.actions;

export default othersDataSlice.reducer;
